import errorIcon from '@p2p/assets/img/error_icon.png';
import pendingIconPathImg from '@p2p/assets/img/pending_icon.png';
import { LoadingIcon, SbpIcon } from '@p2p/assets/svg';
import {
  CopyIconButton,
  Loading,
  MainContainer,
  ModalIncrementAppliedV1,
  Timer,
} from '@p2p/components';
import { FailTransfer, SuccessTransfer, TransferAction } from '@p2p/entities/transfer';
import { useDeepLink } from '@p2p/pages/Payment/components/DeepLink/useDeepLink';
import { useGetMerchantOrderQuery } from '@p2p/services';
import {
  failStatuses,
  isTerminalOrderStatus,
  PaymentType,
  successStatuses,
} from '@p2p/shared/constants';
import { formatPhoneNumber } from '@p2p/shared/functions';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import styles from './TransferPage.module.scss';
import { TransferPageProps } from './TransferPage.types';
import { SBER_BANK_LABEL, TINKOFF_BANK_LABEL } from './TransferPage.utils';

const ORDER_POLLING_INTERVAL = 1000;

export const TransferPage = ({ openModal, isPlayed }: TransferPageProps) => {
  const { id } = useParams();
  const [pollingInterval, setPollingInterval] = useState<number>(ORDER_POLLING_INTERVAL);
  const {
    data: order,
    isLoading: isOrderLoading,
    isSuccess,
  } = useGetMerchantOrderQuery(id!, { pollingInterval, skip: !id });
  const { isRmFlow } = useDeepLink({ order });
  const inputTransferLabel = isRmFlow ? 'Номер договора' : 'Номер счета';
  const status = order?.status;
  const isTransfer = order?.requisite?.payment_type === 'TRANSFER';

  // P2P-2776: RTK Query берёт минимальный pollingInterval среди подписчиков, поэтому
  // останавливать опрос нужно и здесь, и в LandingRenderContent -- иначе оставшийся
  // подписчик продолжит поллить заявку, уже перешедшую в конечный статус.
  useEffect(() => {
    if (isTerminalOrderStatus(status)) {
      setPollingInterval(0);
    }
  }, [status]);

  const isShowGuideTinkoff = useMemo(
    () => order?.payment_form?.slice(-TINKOFF_BANK_LABEL.length) === TINKOFF_BANK_LABEL,
    [order?.payment_form],
  );

  const isShowGuideSber = useMemo(
    () =>
      order?.payment_form?.slice(-SBER_BANK_LABEL.length) === SBER_BANK_LABEL &&
      order?.requisite?.payment_type === PaymentType.PAYMENT_TYPE_SBP,
    [order?.payment_form, order?.requisite?.payment_type],
  );

  const phoneNumber = order?.requisite?.phone_number ? order.requisite.phone_number.toString() : '';

  const accountNumber = order?.requisite?.account_number
    ? order?.requisite?.account_number.toString()
    : '';

  if (isOrderLoading) {
    return <Loading />;
  }

  if (successStatuses.includes(String(status))) {
    return <SuccessTransfer url={String(order?.merchant_success_page)} />;
  }

  if (failStatuses.includes(String(status))) {
    return <FailTransfer url={String(order?.merchant_failed_page)} />;
  }

  return (
    <MainContainer>
      {order?.id && order?.increment_applied && (
        <ModalIncrementAppliedV1
          uid={order?.id}
          sumText={`${order?.expected_income.toLocaleString('ru-RU')} ${order?.currency_code_to}`}
        />
      )}
      <div className={styles.wrapper}>
        <h1>Сделайте перевод по указанным реквизитам</h1>
        {order?.requisite?.payment_type === 'SBP' && (
          <>
            <div className={classNames(styles.warningRed, styles.warningSBP)}>
              <img src={errorIcon} alt="error icon" />
              <p>
                Важно! Перевод необходимо выполнить строго на банк получателя средств, иначе
                денежные средства будут утеряны
              </p>
            </div>
            <div className={styles.line} />
          </>
        )}
        <div className={styles.warningRed}>
          <img src={errorIcon} alt="error icon" />
          <p>Автоматическое зачисление платежа осуществляется по указанной ниже точной сумме</p>
        </div>
        {order?.status === 'REQUISITES_NOT_FOUND' && (
          <p className={styles.warningRed}>{order?.status}</p>
        )}
        {isShowGuideTinkoff && (
          <div className={styles.inputPaygo}>
            <label className={styles.input_field}>
              <div className={styles.input_field_label}>Инструкция</div>
              <div className={styles.payment_dialog__platform_text}>
                <ul style={{ paddingLeft: '16px' }}>
                  <li>Перейдите в приложение Т-банка (Tinkoff)</li>
                  <li>
                    Далее выберите раздел &#34;Платежи&#34; и&#34;Перевод по номеру телефона&#34;
                  </li>
                  <li>Укажите страну, в которую необходимо сделать перевод</li>
                  <li>Выберите банк {order?.requisite?.bank?.name} и отправьте точную сумму.</li>
                </ul>
              </div>
            </label>
          </div>
        )}
        {isShowGuideSber && (
          <div className={styles.inputPaygo}>
            <label className={styles.input_field}>
              <div className={styles.input_field_label}>Инструкция</div>
              <div className={styles.payment_dialog__platform_text}>
                <ul style={{ paddingLeft: '16px' }}>
                  <li>Перейдите в приложение СберБанк</li>
                  <li>Зайдите в меню &#34;Платежи&#34; - &#34;Оплатить мобильную связь&#34;</li>
                  <li>Выберите страну, в которую нужно сделать перевод</li>
                  <li>Вставьте номер телефона получателя</li>
                  <li>
                    Выберите оператора <b>{order?.requisite?.bank?.name}</b>
                  </li>
                  <li>Отправьте точную сумму</li>
                </ul>
              </div>
            </label>
          </div>
        )}
        <div className={styles.info}>
          <div className={styles.item}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <span>Банк получателя</span>
                <p>{order?.requisite?.bank?.name || 'Банк не найден'}</p>
              </div>
              {order?.requisite?.payment_type === 'SBP' && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <SbpIcon width={22} height={22} />
                </div>
              )}
            </div>
          </div>
          <div className={styles.item}>
            <div>
              <span>{isTransfer ? inputTransferLabel : 'Реквизиты для перевода'}</span>
              <p>
                {isSuccess && order?.requisite?.payment_type === 'SBP'
                  ? formatPhoneNumber(order?.requisite.phone_number)
                  : order?.requisite?.account_number}
              </p>
            </div>
            <CopyIconButton
              text={order?.requisite?.payment_type === 'SBP' ? phoneNumber : accountNumber}
            />
          </div>
          {isTransfer && (
            <>
              {!isRmFlow && (
                <div className={styles.item}>
                  <div>
                    <span>БИК</span>
                    <p>{order?.requisite?.bic ?? '-'}</p>
                  </div>
                  <CopyIconButton text={order?.requisite.bic} />
                </div>
              )}
              <div className={styles.item}>
                <div>
                  <span>Назначение платежа</span>
                  <p>Перевод</p>
                </div>
                <CopyIconButton text="Перевод" />
              </div>
              <div className={styles.item}>
                <div>
                  <span>Получатель</span>
                  <p>{order?.requisite.fio}</p>
                </div>
              </div>
            </>
          )}
          <div className={styles.item}>
            <div>
              <span>Сумма для перевода</span>
              <p>
                {order?.expected_income} {order?.currency_code_to}
              </p>
            </div>
            <CopyIconButton text={order?.expected_income.toString() || ''} />
          </div>
        </div>
        <div className={styles.notFilledItem}>
          <span>Номер заявки</span>
          <p>{id}</p>
        </div>
        {Number(order?.time_left) <= 600 && (
          <p className={styles.warning}>
            <img src={pendingIconPathImg} alt="pending icon" />
            Ваша заявка ожидает подтверждения
          </p>
        )}
        <div className={styles.info}>
          <div className={styles.item}>
            <div>
              <span>Ожидаем ваш платеж</span>
              <Timer seconds={Number(order?.time_left)} className={styles.timer} />
            </div>
          </div>
        </div>
        {isPlayed ? (
          <button type="button" className={classNames(styles.btnDisabled, styles.btn)}>
            Поиск платежа{' '}
            <span className={styles.loader}>
              <LoadingIcon />
            </span>
          </button>
        ) : (
          <>
            <TransferAction order={order} />
            <button type="button" className={styles.btn} onClick={openModal}>
              Я оплатил
            </button>
          </>
        )}
      </div>
    </MainContainer>
  );
};
